/**
 * Which laps go into a 3D ghost race, from wherever the button was pressed.
 *
 * The 3D view takes 1-4 laps. Every entry point (the analysis toolbar, the lap
 * selector, the leaderboard) has to answer the same question — "which four?" —
 * and it should answer it identically, or the button behaves differently
 * depending on where you found it.
 *
 * The rule, in order:
 *   1. Every driver present gets their fastest lap first. With two drivers
 *      loaded and three laps ticked each, taking the four fastest overall could
 *      return four laps from one driver and silently drop the other — which is
 *      not what anyone means by "compare these".
 *   2. Remaining slots fill from the leftovers, fastest first.
 *   3. The result is ordered by lap time, so the reference car (fastest) is
 *      first and the colors read consistently.
 *
 * With a single session this degenerates to exactly "the four fastest selected
 * laps", which is the common case.
 */

export const LAP3D_MAX = 4;

/**
 * @param drivers [{ sessionId, name, car, laps: [{ lapIndex, timeS, color, label }] }]
 * @returns { picked: [...], dropped: number }
 */
export function pickLap3dLaps(drivers = []) {
  const pools = [];
  for (const d of drivers) {
    if (!d?.sessionId) continue;
    const laps = (d.laps || [])
      .filter((l) => l && Number.isFinite(l.timeS) && Number.isInteger(l.lapIndex))
      .map((l) => ({ ...l, sessionId: d.sessionId, name: d.name, car: d.car }))
      .sort((a, b) => a.timeS - b.timeS);
    if (laps.length) pools.push(laps);
  }
  if (!pools.length) return { picked: [], dropped: 0 };

  const picked = [];
  // One per driver first — guarantees coverage before depth.
  for (const pool of pools) {
    if (picked.length >= LAP3D_MAX) break;
    picked.push(pool.shift());
  }
  // Then fill from whatever is left, fastest first.
  const rest = pools.flat().sort((a, b) => a.timeS - b.timeS);
  while (picked.length < LAP3D_MAX && rest.length) picked.push(rest.shift());

  picked.sort((a, b) => a.timeS - b.timeS);
  return { picked, dropped: rest.length };
}

/**
 * Display name for one car in the HUD.
 *
 * Four cars all labelled "Joe" is useless, so when several laps come from the
 * same driver the lap number disambiguates them. With one lap per driver the
 * name stands alone, which is what the head-to-head case wants.
 */
export function lap3dCarName(entry, sameDriverCount) {
  const base = entry.name || 'Driver';
  if (sameDriverCount > 1 && entry.label) return `${base} · ${entry.label}`;
  return base;
}

/** Turn the picked laps into the request body the 3D endpoint expects. */
export function toLap3dRequest(picked) {
  const counts = new Map();
  for (const p of picked) counts.set(p.sessionId, (counts.get(p.sessionId) || 0) + 1);
  return picked.map((p) => ({
    session_id: p.sessionId,
    lap_index: p.lapIndex,
    name: lap3dCarName(p, counts.get(p.sessionId)),
    car: p.car || undefined,
    color: p.color || undefined,
  }));
}
